import React, { useState, useEffect } from 'react';

import Select from "react-select";
import { ReactComponent as Pointer } from "../icons/cursor.svg";
import { ReactComponent as Hand } from "../icons/hand.svg";
import { ReactComponent as Minus } from "../icons/minus.svg";
import { ReactComponent as Plus } from "../icons/plus.svg";

import { GrowthPeriod, IndexMatrix, MatrixFile } from "../types";
import { getShortMatrixName } from "../utilities";

import './Topbar.css';

interface Props {
    zoom: number;
    dragMode: boolean;
    showReturns: boolean;
    applyInflation: boolean;
    currentMatrixId: string;
    currentMatrix: IndexMatrix;
    selectText: string;
    matrixFiles: any;
    growthPeriod: GrowthPeriod;
    handleZoom: (zoom: number) => void;
    handleDraggable: (dragMode: boolean) => void;
    handleReset: () => void;
    handleShowReturns: (showReturns: boolean) => void;
    handleSelectInflation: (applyInflation: boolean) => void;
    handleSelectMatrix: (value: any) => void;
    handleTimeframeSubmit: (coordinates: [number, number]) => void;
    highlightReturnsPeriod: [number, number];
    handleSelectTimeframe: (timeframe: [number, number]) => void;
};

interface XYMap {
    [value: string]: number | string;
}

interface Group {
    [key: string]: [
        {
        value: string;
        label: string;
        group: string;
        groupOrder: number;
        indexOrder: number;
        }
    ];
}
  

function Topbar({ 
    zoom,
    dragMode,
    showReturns,
    applyInflation,
    currentMatrixId,
    currentMatrix,
    selectText,
    matrixFiles,
    growthPeriod,
    handleZoom,
    handleDraggable,
    handleReset,
    handleShowReturns,
    handleSelectInflation,
    handleSelectMatrix,
    handleTimeframeSubmit,
    highlightReturnsPeriod,
    handleSelectTimeframe
}: Props) {
    // Group similar indexes in the dropdown and sort them by a predefined order
    let groups: Group = {};

    matrixFiles.forEach((item: MatrixFile) => {
        const { value, formattedLabel, group, groupOrder, indexOrder } = item;

        if (!groups[group]) {
        groups[group] = [] as any;
    }

    groups[group].push({
        value,
        label: formattedLabel,
        group,
        groupOrder,
        indexOrder,
        });
    });

    const groupedOptions = Object.entries(groups)
        .map(([key, value]) => {
        const { groupOrder } = value[0];
        const sortedOptions = value.sort((a, b) =>
            a.indexOrder > b.indexOrder ? 1 : -1
        );
        return {
            label: key,
            options: sortedOptions,
            groupOrder: groupOrder,
        };
        })
        .sort((a, b) => (a.groupOrder > b.groupOrder ? 1 : -1));
        const selectIndexStyles = {
            // @ts-ignore
            groupHeading: (styles, state) => ({
                ...styles,
                fontSize: "13px",
                fontWeight: "bold",
                color: "black",
                marginLeft: '-20px',
                borderTop:'1px solid #eee',
                paddingTop: '10px',
                marginTop: '10px',            }),
            group: (styles, state) => ({
                paddingLeft:'20px'
            })
            
    };

    const currentMatrixName = () => {
        return currentMatrix.meta.name;
    }

    const zoomIn = () => {
      if (zoom === 2) return;
      const newZoom = zoom+0.25;
      handleZoom(newZoom);
    }

    const zoomOut = () => {
      const newZoom = zoom === 1 ? 1 : zoom-0.25;
      handleZoom(newZoom);
    }
    
    const setZoom = (zoom) => {
      handleZoom(zoom.value);
    }

    const disableDrag = () => {
      handleDraggable(false,);
    }

    const enableDrag = () => {
      handleDraggable(true);
    }

    const zoomOptions = Array.from(Array(5).keys()).map((val) => {
        const defaultZoom = 1;
        return { value: defaultZoom + (0.25*val*defaultZoom), label: (defaultZoom+(0.25*val*defaultZoom))*100 + '%'}
    });

    const returnsOptions = [{ value: true, label: 'Yes'},{ value: false, label: 'No'}]
    const inflationOptions = [{ value: 'before', label: 'Before'},{ value: 'after', label: 'After'}]

    const timeframesToHighlight = [
        { value: "[0,0]", label: "None" },
        { value: "[0,5]", label: "1-5" },
        { value: "[5,10]", label: "6-10" },
        { value: "[10,15]", label: "11-15" },
    ];

    const timeframeOptions = timeframesToHighlight.map(({ value, label }, index) => {
        return { value: value, label: 'Hightlight Year Range: ' + label, index }
    });

    const handleChangeShowReturns = (
        returnOption
    ) => {
        return returnOption.value ? handleShowReturns(true) : handleShowReturns(false);
    };

    const handleChangeInflation = (
        inflationOption
    ) => {
        return inflationOption.value === "after"
            ? handleSelectInflation(true)
            : handleSelectInflation(false);
    };

    const {
        meta: { timePeriod },
    } = currentMatrix;
    const [startYear, endYear] = timePeriod; // index matrix time period
    const [start, end] = growthPeriod; // selected time period
    const numberOfRows = endYear - startYear;

    let xMap: XYMap = {}; // TO
    let yMap: XYMap = {}; // FROM
    let tempStart = startYear;
    let tempEnd = endYear;

    // equivalent to do N times
    Array.from({ length: numberOfRows + 1 }).forEach((item, index) => {
        xMap[tempStart] = index;
        yMap[tempEnd] = index;
        tempStart = tempStart + 1;
        tempEnd = tempEnd - 1;
    });

    const [timeframeFrom, setTimeframeFrom] = useState<number>(start);
    const [timeframeTo, setTimeframeTo] = useState<number>(end);

    useEffect(() => {
    // the selected time period
        const [start, end] = growthPeriod;
        setTimeframeFrom(start);
        setTimeframeTo(end);
    }, [growthPeriod]);

    const handleChangeTimeframeFrom = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { value } = event.target;
        setTimeframeFrom(parseInt(value));
    };
    const handleChangeTimeframeTo = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { value } = event.target;
        setTimeframeTo(parseInt(value));
    };

    const handleSubmitTimeframe = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
    // need to check if timeframe is valid
        const from = xMap[timeframeFrom];
        const to = yMap[timeframeTo];

        // check if they are numbers because checking for 0 && 0 will not work
        if (typeof from === "number" && typeof to === "number") {
            handleTimeframeSubmit([to, from]);
        }
        event.preventDefault();
        return false;
    };

    const onMenuOpen = () => {
        const currentLabel = getShortMatrixName(currentMatrixName());
        setTimeout(() => {
            const menuList = document.querySelectorAll('[id$="-listbox"]')[0];
            if (!menuList) return;
            const menuContainer = menuList.childNodes[0] as HTMLElement;
            const menuItems = document.querySelectorAll('[class$="-option"]');
            menuItems.forEach((item) => {
              const thisItem = item as HTMLElement;
              if (thisItem.innerText.trim() === currentLabel.trim()) {
                let menuTop =  thisItem.getBoundingClientRect().top - 100;
                if (menuTop < 200) menuTop = 0;
                menuContainer.scrollTop = menuTop;
                return;
              }
            });
          }, 10);
    }

    return (
        <div className="topbar">
            <img className="logo" style={{ paddingRight:'10px' }} src="data:image/png;base64,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" alt="Interact FA Logo" />
            <Select
                name="index-selector"
                inputId="index-selector"
                className="index-selector index"
                value={{value: currentMatrixId, label: currentMatrixName()}}
                defaultValue={{value: currentMatrixId, label: currentMatrixName()}}
                placeholder={'Index:  ' + getShortMatrixName(currentMatrixName())}
                options={groupedOptions}
                isSearchable={true}
                isClearable={false}
                styles={selectIndexStyles}
                onChange={(value) => 
                    handleSelectMatrix(value)
                }
                // @ts-ignore
                onMenuOpen={onMenuOpen}
            />
            <div className="form__group year-range">
                <div className="form__group--inline">
                    <input
                    type="number"
                    name="timeframe_from"
                    // @ts-ignore
                    placeholder={
                        start
                    } /* display the current timeframe as the placeholder */
                    min={
                        startYear
                    } /* min and max should the min and max of the matrix, not the selected */
                    max={endYear}
                    onChange={handleChangeTimeframeFrom}
                    value={timeframeFrom}
                    />
                    &nbsp;-&nbsp;
                    <input
                    type="number"
                    name="timeframe_to"
                    // @ts-ignore
                    placeholder={
                        end
                    } /* display the current timeframe as the placeholder */
                    min={
                        startYear
                    } /* min and max should the min and max of the matrix, not the selected */
                    max={endYear}
                    onChange={handleChangeTimeframeTo}
                    value={timeframeTo}
                    />
                <button
                    className="button button-sm button-primary"
                    // disabled={!timeframeFrom || !timeframeTo}
                    onClick={handleSubmitTimeframe}
                >
                    Update
                </button>
            </div>
        </div>

        <div className="form__group--inline divider">
            <label className="radio-inline">
            Show Returns?
            </label>
            <Select 
            className="index-selector toggle"
            options={returnsOptions} 
            value={{ value: showReturns, label: showReturns ? 'Yes' : 'No' }}
            onChange={(value) => handleChangeShowReturns(value)}
             />
        </div>
        <div className="form__group--inline divider">
            <div className="label-sm">Inflation:</div>
            <Select 
            className="index-selector toggle inflation"
            options={inflationOptions} 
            value={{ value: applyInflation ? 'after' : 'before', label: applyInflation ? 'After' : 'Before' }}
            onChange={(value) => handleChangeInflation(value)}
            />
        </div>

        <div className="form__group--inline divider">
            <div className="label-sm">Highlight:</div>
            <Select 
            className="index-selector toggle"
            options={timeframeOptions} 
            value={{ value: JSON.stringify(highlightReturnsPeriod), label: 'Year Range: ' + (highlightReturnsPeriod[1] === 0 ? 'None' : highlightReturnsPeriod[0]+1+'-'+highlightReturnsPeriod[1]) }}
            onChange={(value) => handleSelectTimeframe(value ? JSON.parse(value.value) : [0,0])}
            />
        </div>
        <div className="divider"></div>
            <Select 
            className="index-selector zoom"
            options={zoomOptions} 
            value={{ value: zoom, label: zoom*100+'%' }}
            onChange={(value) =>
            setZoom(value)
            }
             />
            <div className="zoom-controls">
            <Minus onClick={zoomOut}></Minus>
            <Plus onClick={zoomIn}></Plus>
            <Pointer className={ !dragMode ? 'selected' : '' } onClick={disableDrag}></Pointer>
            <Hand className={ dragMode ? 'selected' : '' } onClick={enableDrag}></Hand>
            </div>
        </div>
    );
  }


export default Topbar;
